import { motion } from 'framer-motion'
import { Wrench, Clock, Layers } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Application from '../pages/Application';

const ApplicationGuide = () => {
    const navigate = useNavigate()
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="w-full py-16 bg-gradient-to-br from-green-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Colonne de gauche - Photo du produit */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-1 hover:rotate-0 transition-transform duration-300 border border-green-200">
              <div className="relative group">
                <img
                  src="images/ecotherm.png"
                  alt="Sac ECOTHERM - Produit prêt à l'emploi"
                  className="w-full h-auto rounded-xl shadow-lg object-cover transform group-hover:scale-105 transition-transform duration-500"
                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/500x600/4ADE80/FFFFFF?text=ECOTHERM+25kg";
                  }}
                />
                {/* Overlay d'effet */}
                <div className="absolute inset-0 bg-gradient-to-t from-green-50/20 to-transparent rounded-xl group-hover:from-green-100/30 transition-all duration-500"></div>
              </div>
              
              {/* Badge produit */}
              <div className="absolute -top-3 -left-3 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg transform -rotate-6">
                <span className="text-sm font-bold">PRÊT À L'EMPLOI</span>
              </div>
              
              {/* Poids du sac */}
              <div className="absolute -bottom-3 -right-3 bg-orange-500 text-white px-4 py-2 rounded-full shadow-lg transform rotate-6">
                <span className="text-sm font-bold">20 kg</span>
              </div>
            </div>
          </motion.div>

          {/* Colonne de droite - Mode d'emploi */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-8"
          >
            {/* En-tête */}
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4"
              >
                <Wrench className="w-8 h-8 text-green-600" />
              </motion.div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Mode d'Emploi
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Application simple en <strong className="text-green-600">2 étapes</strong> sans ajout d'eau
              </p>
            </div>

            {/* Étapes d'application */}
            <div className="space-y-6">
              {/* Étape 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-xl">1</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Layers className="w-5 h-5 text-blue-500" />
                      <h3 className="text-xl font-bold text-gray-900">Première Couche</h3>
                    </div>
                    <div className="space-y-3">
                      <p className="text-gray-700">
                        <strong>Mélange :</strong> Au malaxeur (700-900 tr/min) pendant 2 minutes
                      </p>
                      <p className="text-gray-700">
                        <strong>Application :</strong> À la lisseuse ou machine à projeter
                      </p>
                      <p className="text-gray-700">
                        <strong>Épaisseur :</strong> 0,5 cm uniforme
                      </p>
                      <div className="flex items-center gap-2 text-sm text-blue-600 font-semibold">
                        <Clock className="w-4 h-4" />
                        <span>💧 Aucun ajout d'eau nécessaire</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Temps de séchage */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="bg-yellow-50 rounded-xl p-4 border border-yellow-200"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="text-yellow-800 font-semibold">
                      ⏱️ Temps de séchage : 24 heures
                    </p>
                    <p className="text-yellow-700 text-sm">
                      Variable selon température et humidité ambiante
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Étape 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-xl">2</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Layers className="w-5 h-5 text-green-500" />
                      <h3 className="text-xl font-bold text-gray-900">Seconde Couche</h3>
                    </div>
                    <div className="space-y-3">
                      <p className="text-gray-700">
                        <strong>Application :</strong> Même procédé que la première couche
                      </p>
                      <p className="text-gray-700">
                        <strong>Épaisseur :</strong> 0,5 cm supplémentaire
                      </p>
                      <p className="text-gray-700">
                        <strong>Résultat :</strong> Épaisseur totale de 1 cm
                      </p>
                      <div className="bg-green-50 rounded-lg p-3 mt-2">
                        <p className="text-green-800 text-sm font-semibold">
                          ✅ Isolation optimale atteinte
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Conseils rapides */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="bg-white rounded-xl p-4 shadow-md border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm">⚠️</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Surface Préparée</h4>
                    <p className="text-gray-600 text-xs">Couche de dégrossissage au ciment requise</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-md border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm">💧</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Sans Eau</h4>
                    <p className="text-gray-600 text-xs">Ne jamais ajouter d'eau au mélange</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7 }}
              className="text-center lg:text-left pt-4"
            >
              <button  onClick={() => navigate('/application')}  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                Voir le guide complet d'application
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export default ApplicationGuide